import * as React from 'react';
import { observer } from 'mobx-react';

import { DangerTag, IBasicTableColumn, IconTextButton, SuccessTag, WarningTag } from 'ui-lib';
import { getErrorMessage } from 'common-utils';
import { IDeviceTableProps } from './components';
import { DeviceReportTable } from './device-report-table';
import { DeviceContainersStore } from 'store/device-details/device-containers.store';
import { CONTAINER_STATE_FILTERS, ContainerDto } from 'dto/container.dto';
import { TableUtils } from 'pages/private/components/table-utils';
import { showConfirmation, showNotification } from '../utils';
import { useDeviceStore } from 'store';

const containerStateTag = (container: ContainerDto): JSX.Element => {
    if (container.isRunning) return <SuccessTag value={container.stateLabel} />;
    if (container.isStopped) return <DangerTag value={container.stateLabel} />;
    return <WarningTag value={container.stateLabel} />;
};

const OFFLINE_ACTION_MESSAGE = 'Containers can only be started/stopped while the device is online.';

interface IContainersTableProps extends IDeviceTableProps {
    isOnline?: boolean;
}

export const ContainersTable = observer((options: IContainersTableProps) => {
    const devicesStore = useDeviceStore();
    const store = React.useMemo(
        () =>
            {
                const store = new DeviceContainersStore({
                    accountId: options.accountId,
                    uid: options.uid,
                    devicesStore: devicesStore,
                    serialNumber: options.serialNumber,
                });
                store.initialize();
                return store;
            },
        [],
    );
    const pendingContainerIds = store.pendingContainerIds;

    const isOnlineRef = React.useRef(!!options.isOnline);
    isOnlineRef.current = !!options.isOnline;

    const ensureOnline = (): boolean => {
        if (!isOnlineRef.current) showNotification('warning', OFFLINE_ACTION_MESSAGE);
        return isOnlineRef.current;
    };

    const onAction = (container: ContainerDto) => {
        if (!ensureOnline()) return;

        const shouldStop = container.isRunning;

        showConfirmation(`Are you sure you want to ${shouldStop ? 'stop' : 'start'} the container "${container.names}"?`, async () => {
            if (!ensureOnline()) return;

            showNotification('info', `The container "${container.names}" is being ${shouldStop ? 'stopped' : 'started'}.`);
            try {
                if (shouldStop) {
                    await store.stopContainer(container);
                } else {
                    await store.startContainer(container);
                }
                showNotification('success', `The container "${container.names}" has been ${shouldStop ? 'stopped' : 'started'}.`);
            } catch (error) {
                showNotification('error', getErrorMessage(error));
            }
        });
    };

    const columns: IBasicTableColumn[] = [
        TableUtils.createTruncatedTextColumn({ key: 'id', title: 'ID' }),
        TableUtils.createBasicFilterColumn({ key: 'names', title: 'Container Name', defaultSortOrder: 'ascend' }),
        TableUtils.createBasicEnumFilterColumn({
            key: 'state',
            title: 'State',
            filters: CONTAINER_STATE_FILTERS,
            columnExtraOptions: { render: (_, record: ContainerDto) => containerStateTag(record) },
        }),
        {
            code: 'actions',
            title: 'Action',
            dataIndex: 'id',
            render: (_, record: ContainerDto) => (
                <IconTextButton
                    label={record.isRunning ? 'Stop' : 'Start'}
                    type='link'
                    disabled={!options.isOnline || pendingContainerIds.has(record.id)}
                    tooltip={!options.isOnline ? OFFLINE_ACTION_MESSAGE : undefined}
                    onClick={() => onAction(record)}
                />
            ),
        },
    ];

    return <DeviceReportTable store={store} table={{ columns, dataSource: store.currentContainersListing, rowKey: 'id' }} />;
});
